import { useEffect, useRef, useState } from "react";
import axios from "axios";
import * as c from "../../../constants";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "../../../language";
import ButtonForm from "../../../components/Button/ButtonForm";

function normalizeQuestions(data) {
  const list = Array.isArray(data) ? data : data?.questions || [];
  return list
    .map((item) => {
      const q = item.question || item;
      return {
        id: q.id,
        code: q.code,
        text: q.text,
        question_type: q.question_type,
        options: q.options || [],
        required: item.required ?? q.required ?? true,
        order: item.order ?? q.order ?? 0,
      };
    })
    .sort((a, b) => a.order - b.order);
}

export default function Feedback() {
  const theme = useTheme();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const questionRefs = useRef({});

  useEffect(() => {
    let isMounted = true;

    axios
      .get(`${c.API_URL}/api/feedback/response/`)
      .then(() => {
        if (!isMounted) return;
        setAlreadySubmitted(true);
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        if (err.response?.status === 404) {
          axios
            .get(`${c.API_URL}/api/feedback/questions/`)
            .then((res) => {
              if (!isMounted) return;
              setQuestions(normalizeQuestions(res.data));
              setLoading(false);
            })
            .catch(() => {
              if (isMounted) {
                setError(true);
                setLoading(false);
              }
            });
        } else {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (code, value) =>
    setAnswers((prev) => ({ ...prev, [code]: value }));

  const isQuestionVisible = (q) => {
    if (q.code === "F8_OTHER") {
      const f8Value = answers["F8"];
      return Array.isArray(f8Value) && f8Value.includes("other");
    }
    return true;
  };

  const visibleQuestions = questions.filter(isQuestionVisible);
  const otherQuestion = questions.find((q) => q.code === "F8_OTHER");
  const displayQuestions = visibleQuestions.filter((q) => q.code !== "F8_OTHER");

  const isAnswered = (q) => {
    if (!q.required) return true;
    const value = answers[q.code];
    if (q.question_type === "multi") return Array.isArray(value) && value.length > 0;
    return value !== undefined && value !== null && value !== "";
  };

  const answeredCount = visibleQuestions.filter(isAnswered).length;
  const progress =
    visibleQuestions.length > 0
      ? Math.round((answeredCount / visibleQuestions.length) * 100)
      : 0;

  const scrollToFirstUnanswered = () => {
    const first = visibleQuestions.find((q) => !isAnswered(q));
    if (first && questionRefs.current[first.code]) {
      questionRefs.current[first.code].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleSubmit = async () => {
    if (answeredCount !== visibleQuestions.length) {
      scrollToFirstUnanswered();
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const answersPayload = visibleQuestions
        .filter((q) => answers[q.code] !== undefined && answers[q.code] !== null && answers[q.code] !== "")
        .map((q) => ({ question: q.id, value: answers[q.code] }));

      await axios.post(`${c.API_URL}/api/feedback/submit/`, { answers: answersPayload });

      setJustSubmitted(true);
    } catch (err) {
      if (err.response?.status === 400) {
        setAlreadySubmitted(true);
        return;
      }
      setSubmitError(err.response?.data?.detail || t("bt_results_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (q, idx) => {
    const accentColor = idx % 2 === 0 ? theme.palette.tertiary.C300 : theme.palette.quartiary.C300;

    return (
      <Box
        key={q.id}
        sx={{
          border: "1px solid",
          borderRadius: 4,
          overflow: "hidden",
          borderLeft: `10px solid ${accentColor}`,
          mb: 1.5,
          p: 2,
          ...(theme.palette.mode === "light"
            ? { bgcolor: "grey.C50", borderColor: "grey.C900" }
            : {
                bgcolor: "greydark.C900",
                borderColor: "greydark.C400",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
              }),
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          ref={(el) => {
            questionRefs.current[q.code] = el;
            if (q.code === "F8") questionRefs.current["F8_OTHER"] = el;
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: `2px solid ${accentColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: "2px",
            }}
          >
            <Typography
              variant="number4"
              sx={{ color: accentColor, fontWeight: 700, fontSize: "0.75rem", lineHeight: 1 }}
            >
              {idx + 1}
            </Typography>
          </Box>

          <FormControl fullWidth>
            <FormLabel>
              {q.text} {q.required && "*"}
            </FormLabel>

            {q.question_type === "text" && (
              <TextField
                multiline
                required={q.required}
                value={answers[q.code] || ""}
                onChange={(e) => handleChange(q.code, e.target.value)}
                sx={{ mt: 2 }}
              />
            )}

            {q.question_type === "number" && (
              <TextField
                type="number"
                value={answers[q.code] || ""}
                onChange={(e) => handleChange(q.code, Number(e.target.value))}
                sx={{ mt: 2 }}
              />
            )}

            {q.question_type === "single" && (
              <RadioGroup
                value={answers[q.code] || ""}
                onChange={(e) => handleChange(q.code, e.target.value)}
              >
                {q.options.map((opt) => (
                  <FormControlLabel key={opt.value} value={opt.value} control={<Radio />} label={opt.label} />
                ))}
              </RadioGroup>
            )}

            {q.question_type === "multi" && (
              <>
                {q.options.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Checkbox
                        checked={(answers[q.code] || []).includes(opt.value)}
                        onChange={(e) => {
                          const current = answers[q.code] || [];
                          handleChange(
                            q.code,
                            e.target.checked ? [...current, opt.value] : current.filter((v) => v !== opt.value)
                          );
                        }}
                      />
                    }
                    label={opt.label}
                  />
                ))}

                {q.code === "F8" && otherQuestion && (answers["F8"] || []).includes("other") && (
                  <TextField
                    multiline
                    required={otherQuestion.required}
                    label={otherQuestion.text}
                    value={answers[otherQuestion.code] || ""}
                    onChange={(e) => handleChange(otherQuestion.code, e.target.value)}
                    fullWidth
                    sx={{ mt: 2 }}
                  />
                )}
              </>
            )}

            {q.question_type === "boolean" && (
              <RadioGroup
                value={answers[q.code] || ""}
                onChange={(e) => handleChange(q.code, e.target.value.toLowerCase())}
              >
                {q.options.map((opt) => (
                  <FormControlLabel
                    key={opt.id ?? opt.value}
                    value={String(opt.value).toLowerCase()}
                    control={<Radio />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
            )}

            {q.question_type === "scale" && (
              <Slider
                min={1}
                max={10}
                step={1}
                marks
                value={answers[q.code] ?? 1}
                onChange={(_, value) => handleChange(q.code, value)}
                valueLabelDisplay="auto"
                sx={{
                  mt: 3,
                  "& .MuiSlider-valueLabel": { fontSize: "1.2rem", fontWeight: 600 },
                  "& .MuiSlider-markLabel": { fontSize: "1rem" },
                }}
              />
            )}
          </FormControl>
        </Stack>
      </Box>
    );
  };

  const pageWrapperSx = { width: "100%", maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 4 }, pt: { xs: 4, sm: 6 }, pb: 6 };

  if (loading) {
    return (
      <Box sx={{ ...pageWrapperSx, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={pageWrapperSx}>
        <Typography color="error">{t("bt_results_error")}</Typography>
      </Box>
    );
  }

  if (justSubmitted) {
    return (
      <Box sx={pageWrapperSx}>
        <Typography variant="h6" fontWeight={700}>
          {t("feedback_thank_you")}
        </Typography>
      </Box>
    );
  }

  if (alreadySubmitted) {
    return (
      <Box sx={pageWrapperSx}>
        <Typography variant="h6" fontWeight={700}>
          {t("feedback_already_title")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={pageWrapperSx}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={700}>
          {t("feedback_header")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: "pre-line", mt: 1 }}>
          {t("feedback_subheader")}
        </Typography>
      </Box>

      <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
      <Typography variant="subtitle2" align="center" fontWeight={400} sx={{ mb: 4 }}>
        {answeredCount} / {visibleQuestions.length} {t("feedback_answered_label")}
      </Typography>

      {displayQuestions.map(renderQuestion)}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <ButtonForm
        name={submitting ? <CircularProgress size={18} color="inherit" /> : t("feedback_submit")}
        onClick={handleSubmit}
        disabled={submitting}
      />
    </Box>
  );
}
